import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Mail, Plus, UserPlus, X } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { getUserOrganizations } from '../../services/organizationService';

const INVITE_ROLE_LABELS: Record<string, string> = {
  admin: 'Platform Admin',
  org_admin: 'Organisation Admin',
  internal_user: 'Internal User',
  consultant: 'Consultant',
  auditor: 'Auditor',
};

type SendInviteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvitesSent?: () => void;
};

export const SendInviteDialog = ({ open, onOpenChange, onInvitesSent }: SendInviteDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState<Array<{ id: string; name: string; tenant_type?: string }>>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('org_admin');
  const [pending, setPending] = useState<Array<{ email: string; role: string }>>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      setError('');
      setPending([]);
      setEmail('');
      setRole('org_admin');
      setSelectedWorkspace('');
      try {
        const orgData = await getUserOrganizations();
        const list = orgData.organizations.map(o => ({
          id: o.id,
          name: o.name,
          tenant_type: o.tenant_type,
        }));
        setWorkspaces(list);
        if (list.length > 0) setSelectedWorkspace(list[0].id);

        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('base_profiles')
            .select('role')
            .eq('id', user.id)
            .single();
          setIsPlatformAdmin(profile?.role === 'admin');
        }
      } catch {
        setError('Failed to load workspaces.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const handleAddInvite = () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    if (pending.some(inv => inv.email === trimmed)) {
      setError('This email is already in the list.');
      return;
    }
    setError('');
    setPending(prev => [...prev, { email: trimmed, role }]);
    setEmail('');
    setRole('org_admin');
  };

  const handleSend = async () => {
    if (!selectedWorkspace || pending.length === 0) return;
    setSending(true);
    setError('');
    const workspace = workspaces.find(w => w.id === selectedWorkspace);
    const inviteType = workspace?.tenant_type === 'personal' ? 'personal' : 'organisation';
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const token = (await supabase.auth.getSession()).data.session?.access_token;
    const errors: string[] = [];
    for (const inv of pending) {
      try {
        const res = await fetch(`${apiUrl}/api/invites/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            receiver_email: inv.email,
            role: inv.role,
            tenant_id: selectedWorkspace,
            invite_type: inviteType,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          errors.push(`${inv.email}: ${body.error || 'Failed'}`);
        }
      } catch {
        errors.push(`${inv.email}: Network error`);
      }
    }
    setSending(false);
    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      onOpenChange(false);
      onInvitesSent?.();
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setPending([]);
    setEmail('');
    setError('');
    setRole('org_admin');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Invite People
          </DialogTitle>
          <DialogDescription>
            Enter email addresses and select a role to send invitations
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Workspace selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Workspace</label>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading workspaces...</p>
            ) : workspaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">No workspaces found.</p>
            ) : (
              <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a workspace" />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map(w => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name} {w.tenant_type === 'personal' ? '(Personal)' : '(Organisation)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Email input */}
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddInvite(); } }}
              disabled={sending}
            />
            <Button type="button" variant="outline" onClick={handleAddInvite} disabled={sending}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          {/* Role selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Assign Role</label>
            <Select value={role} onValueChange={setRole} disabled={sending}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {isPlatformAdmin && (
                  <SelectItem value="admin">Platform Admin</SelectItem>
                )}
                <SelectItem value="org_admin">Organisation Admin</SelectItem>
                <SelectItem value="internal_user">Internal User</SelectItem>
                <SelectItem value="consultant">Consultant</SelectItem>
                <SelectItem value="auditor">Auditor</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              This role will be assigned to the user within the selected workspace.
            </p>
          </div>

          {/* Pending invites list */}
          {pending.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Pending Invites ({pending.length})</p>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {pending.map((inv, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-2 p-2 bg-muted rounded-md"
                  >
                    <div className="flex items-center space-x-2 min-w-0">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">{inv.email}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="secondary" className="text-xs whitespace-nowrap">
                        {INVITE_ROLE_LABELS[inv.role] ?? inv.role}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPending(prev => prev.filter((_, i) => i !== idx))}
                        disabled={sending}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={handleClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={pending.length === 0 || !selectedWorkspace || sending}
          >
            {sending ? (
              <span>Sending...</span>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send Invites
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
